import React from "react";
import Calculation from "./Calculation.jsx";
import DispatchGanttPreview from "./DispatchGanttPreview.jsx";
import { clockLabel, durationLabel, moneyLabel, numberLabel } from "../../utils/consolidationPresentation.js";
import { sheetBreaks } from "../../utils/consolidationBreaks.js";

export function retainedDate(value, timezone) {
  if (!value) return "Date non conservée";
  if (!timezone) return value;
  return new Intl.DateTimeFormat("fr-CA", { timeZone: timezone, dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

const GROUPS = ["Journée", "Ordre des arrêts", "Arrêts", "Commandes", "Facturation"];
const span = minutes => Math.abs(minutes) < 60 ? `${Math.round(Math.abs(minutes))} min` : durationLabel(minutes);

function changeValue(value, type, timezone, day) {
  if (value == null || value === "") return "Non renseigné";
  if (type === "text" && /^Non (livrée|chargée) \(hors facturation\)$/.test(value)) return value.replace(" (hors facturation)", "");
  if (type === "time") return timezone ? clockLabel(value, timezone, day) : value;
  if (type === "time_ranges") return value.length ? value.map(range => range.map(part => changeValue(part, "time", timezone, day)).join(" → ")).join(" ; ") : "Aucun";
  if (type === "minutes") return durationLabel(Number(value));
  if (type === "money") return moneyLabel(value);
  if (type === "tons") return `${numberLabel(value, 2)} t`;
  if (type === "number") return numberLabel(value, 2);
  if (type === "position") return `${value}${Number(value) === 1 ? "er" : "e"} arrêt`;
  if (typeof value === "boolean") return value ? "Oui" : "Non";
  return Array.isArray(value) ? value.join(" ; ") : String(value);
}

function gap({ type, before, after }) {
  if (!["minutes", "money", "tons"].includes(type) || before == null || after == null) return "";
  const delta = Number(after) - Number(before), sign = delta > 0 ? "+" : "−";
  if (Math.abs(delta) < 0.005) return " (inchangé)";
  return ` (${sign}${type === "minutes" ? span(delta) : type === "money" ? moneyLabel(Math.abs(delta)) : `${numberLabel(Math.abs(delta), 2)} t`})`;
}

// Stop sections already name the stop, so they pass short labels.
function changeText(change, timezone, day, label = change.label) {
  if (change.group === "Ordre des arrêts" && change.type !== "position" && (change.before == null || change.after == null)) {
    return `${change.before == null ? "Arrêt ajouté" : "Arrêt retiré"} : ${change.after ?? change.before}`;
  }
  return `${label} : ${changeValue(change.before, change.type, timezone, day)} → ${changeValue(change.after, change.type, timezone, day)}${gap(change)}`;
}

export function ChangeGroups({ changes, timezone, day, open = false }) {
  return GROUPS.map(group => {
    const rows = changes.filter(change => change.group === group);
    const count = rows.filter(change => change.before !== change.after).length;
    return rows.length ? <details key={group} open={open}><summary className="cursor-pointer">{group} ({count})</summary>
      <ul className="ml-4 list-disc break-words">{rows.map((change, index) => <li key={index}>{changeText(change, timezone, day)}</li>)}</ul>
    </details> : null;
  });
}

const kinds = { loading: "Chargement", delivery: "Livraison", inspection: "Inspection", return: "Retour",
  preload: "Préchargement", tonne_supplement: "Supplément à la tonne", voyage_supplement: "Supplément voyage", fixed_fee: "Tarif fixe",
  dompeur: "Transport à la tonne" };
const states = { performed: "Effectué", not_performed: "Non effectué", inherited: "Hérité", unset: "Non renseigné" };

const ORDER_HEADERS = ["Commande", "Prévu (t)", "Livré (t)", "Statut"];

// A changed value is tinted in its own cell, with the previous value alongside.
const pick = (list, field) => list.find(change => change.label.endsWith(` · ${field}`));
const tint = change => change ? " bg-amber-100" : "";
function Was({ change, timezone, day }) {
  return change ? <span className="ml-2 whitespace-nowrap text-[11px] font-normal text-amber-900">avant : {changeValue(change.before, change.type, timezone, day)}</span> : null;
}

function OrderCells({ order, loading = false, own = [], timezone, day, empty = "Aucune commande" }) {
  if (!order) return <td className="p-1 text-slate-400" colSpan={4}>{empty}</td>;
  const [planned, delivered, status] = ["Prévu (t)", "Livré (t)", "Statut"].map(field => pick(own, field));
  // Sheets derive loaded tons from delivered tons, so a matching loaded change adds nothing.
  const others = own.filter(change => ![planned, delivered, status].includes(change)
    && !(change.label.endsWith(" · Chargé (t)") && delivered?.after === change.after));
  return <>
    <th className="p-1 font-medium"><span className="whitespace-nowrap">{order.label || "Numéro non conservé"}{order.work_order_number ? ` · Bon ${order.work_order_number}` : ""}
      <span className="font-normal"> · {order.source_mill?.name || order.mill?.name || "Meunerie non conservée"}</span></span>
      {others.map((change, key) => <span key={key} className="block font-normal text-amber-900">{changeText(change, timezone, day, change.label.split(" · ").at(-1))}</span>)}</th>
    {[["planned_tons", planned], ["delivered_tons", delivered]].map(([key, change]) => <td className={`whitespace-nowrap p-1 tabular-nums${tint(change)}`} key={key}>
      {order[key] == null ? "Non conservé" : numberLabel(order[key], 4)}<Was change={change} timezone={timezone} day={day} /></td>)}
    <td className={`whitespace-nowrap p-1 ${order.execution_excluded ? "text-red-700" : ""}${tint(status)}`}>{order.execution_excluded
      ? (loading ? "Non chargée" : "Non livrée") : loading ? "Chargée" : "Livrée"}<Was change={status} timezone={timezone} day={day} /></td>
  </>;
}

function Scroll({ label, children }) {
  return <div className="overflow-x-auto" tabIndex={0} role="region" aria-label={label}>
    <table className="w-full !min-w-[48rem] text-left text-xs">{children}</table></div>;
}

function SavedOrders({ orders, loading = false }) {
  return <Scroll label="Défilement des quantités conservées">
    <caption className="sr-only">Quantités conservées</caption>
    <thead><tr>{ORDER_HEADERS.map(label => <th className="p-1" key={label}>{label}</th>)}</tr></thead>
    <tbody>{orders.map((order, index) => <tr className="border-t border-slate-200" key={order.id || index}><OrderCells order={order} loading={loading} /></tr>)}</tbody>
  </Scroll>;
}

export const validationName = revision => revision === 1 ? "Validation initiale" : `Correction ${revision - 1}`;
const since = revision => revision === 1 ? "la validation initiale" : `la correction ${revision - 1}`;

export default function HistoricalValidation({ detail, changes = [], summary, ganttChanged = true }) {
  // A correction opens on what changed; the full sheet stays one click away.
  const changed = detail.revision > 1 && changes.length > 0;
  const [all, setAll] = React.useState(!changed);
  const row = detail.snapshot?.context || {};
  const calculation = detail.snapshot?.calculation;
  const day = row.dispatch_date || row.execution_date || row.delivery_date;
  // Stop and order changes are shown on their stop; the rest stays grouped above.
  const changesFor = visit => changes.filter(change => change.ref != null
    && (change.ref === visit.id || (visit.orders || []).some(order => order.id === change.ref)));
  const onStops = new Set((row.visits || []).flatMap(changesFor));
  const visits = (row.visits || []).slice().sort((a, b) => a.actual_sequence - b.actual_sequence);
  // An order visited twice (loading, then delivery) shows its changes once, on its last stop.
  const home = new Map(visits.flatMap((visit, index) => (visit.orders || []).map(order => [order.id, index])));
  const ownOf = ref => changes.filter(change => change.ref === ref);
  const changedOrders = (visit, index) => (visit.orders || []).filter(order => home.get(order.id) === index && ownOf(order.id).length);
  const shown = visits.map((visit, index) => all || ownOf(visit.id).length > 0 || changedOrders(visit, index).length > 0);
  const clock = value => value == null ? "Non conservé" : row.timezone ? clockLabel(value, row.timezone, day) : value;
  return <section aria-label={`Validation ${detail.revision} conservée`} className="grid min-w-0 gap-3 rounded-[4px] border border-slate-300 bg-slate-50 p-2">
    <header className="text-sm"><strong>{validationName(detail.revision)} · Lecture seule</strong>
      <p className="text-xs text-slate-600">{retainedDate(detail.validated_at, row.timezone)} · {detail.validated_by || "Auteur non conservé"}</p>
      {detail.reason ? <p className="text-xs">Motif : {detail.reason}</p> : null}
      {summary && detail.revision > 1 ? <p className="mt-1 text-xs font-medium">{summary}</p> : null}
    </header>
    {/* An identical correction has nothing new to show: the previous validation holds the same sheet. */}
    {detail.revision > 1 && !changes.length ? null : <>
    {changes.some(change => !onStops.has(change)) ? <div className="grid gap-1 rounded-[4px] border border-amber-300 bg-amber-50 p-2 text-xs">
      <strong>Changements depuis {since(detail.revision - 1)}</strong>
      <ChangeGroups changes={changes.filter(change => !onStops.has(change))} timezone={row.timezone} day={day} open />
    </div> : null}
    <p className="text-xs">{row.driver_name || row.employee_name || "Identité non conservée"} · {day || "Journée non conservée"}
      {row.truck_number || row.truck_label ? ` · Camion ${row.truck_number || row.truck_label}` : ""}</p>
    {detail.kind === "sheet" ? <>
      {ganttChanged ? <DispatchGanttPreview assignment={row.dispatch_assignment} dispatchDate={row.dispatch_date}
        dispatchPeriod={row.dispatch_period} referenceKey={detail.fingerprint} />
        : <p className="text-xs text-slate-500">Gantt du Dispatch identique à {since(detail.revision - 1)}.</p>}
      <p className="flex flex-wrap gap-x-3 gap-y-1 text-xs"><span>Service : {clock(row.service_start)} → {clock(row.service_end)} · Inspection : {row.inspection_minutes == null ? "Non conservée" : `${numberLabel(row.inspection_minutes)} min`}</span>
        <span>Dîner : {row.no_lunch || ("lunch_start" in row && "lunch_end" in row && !row.lunch_start && !row.lunch_end)
        ? "Aucun dîner renseigné" : !("lunch_start" in row || "lunch_end" in row) ? "Non conservé" : `${clock(row.lunch_start)} → ${clock(row.lunch_end)}`}</span>
        {sheetBreaks(row).filter(pause => pause.start && pause.end).map(pause => <span key={pause.start}>Pause : {clock(pause.start)} → {clock(pause.end)} · {pause.paid ? "payée" : pause.paid === false ? "non payée" : "à classer"}</span>)}</p>
      {(row.exclusions || []).map((excluded, index) => <p key={excluded.id || index} className="text-xs">{excluded.kind === "sheet" ? `Tournée du camion ${excluded.truck_number || "?"} retirée du service` : "Préchargement exclu du service"} : {clock(excluded.start_at)} → {clock(excluded.end_at)}</p>)}
      {changed ? <div className="flex flex-wrap items-center gap-2 text-xs">
        <span>{all ? "Relevé complet" : `Arrêts modifiés seulement : ${shown.filter(Boolean).length} sur ${visits.length}`}</span>
        <button type="button" className="rounded-[4px] border border-slate-300 bg-white px-2 py-0.5 font-semibold" aria-pressed={all} onClick={() => setAll(value => !value)}>
          {all ? "Afficher seulement les changements" : "Afficher tout le relevé"}</button>
      </div> : null}
      {/* One table for the whole sheet: stop and times span their order rows. */}
      {row.visits ? <Scroll label="Défilement des arrêts conservés">
        <caption className="sr-only">Arrêts et quantités conservés</caption>
        <thead><tr>{["Arrêt", "Départ", ...ORDER_HEADERS].map(label => <th className="p-1" key={label}>{label}</th>)}</tr></thead>
        {visits.map((visit, index) => {
          if (!shown[index]) return null;
          const stop = ownOf(visit.id), rank = stop.find(change => change.type === "position");
          const [departure, arrival] = ["Départ", "Arrivée"].map(field => pick(stop, field));
          const rest = stop.filter(change => ![rank, departure, arrival].includes(change));
          const listed = all ? visit.orders || [] : changedOrders(visit, index);
          const orders = listed.length ? listed : [null], span = orders.length;
          return <tbody key={visit.id || index} className="border-t-2 border-slate-200">
            {orders.map((order, line) => <tr key={order?.id || line} className={line ? "border-t border-slate-100" : ""}>
              {line ? null : <>
                <th rowSpan={span} className={`whitespace-nowrap p-1 align-top font-semibold${tint(rank)}`}>{index + 1}. {kinds[visit.kind] || visit.kind} · {visit.label || "Lieu non conservé"}
                  {visit.status !== "performed" ? <span className="block font-normal text-amber-800">{states[visit.status] || "État non conservé"}</span> : null}
                  <Was change={rank} /></th>
                <td rowSpan={span} className={`whitespace-nowrap p-1 align-top${tint(departure || arrival)}`}>{visit.arrival ? `${clock(visit.arrival)} → ` : ""}{clock(visit.departure)}
                  <Was change={arrival} timezone={row.timezone} day={day} /><Was change={departure} timezone={row.timezone} day={day} /></td>
              </>}
              <OrderCells order={order} own={order && home.get(order.id) === index ? ownOf(order.id) : []}
                timezone={row.timezone} day={day} empty={visit.orders?.length ? "" : "Aucune commande"} />
            </tr>)}
            {rest.length || visit.notes ? <tr><td colSpan={6} className="px-1 pb-1">
              {rest.map((change, key) => <p key={key} className="text-amber-900">{changeText(change, row.timezone, day, change.label.split(" · ").at(-1))}</p>)}
              {visit.notes ? <p className="whitespace-pre-wrap">{visit.notes}</p> : null}
            </td></tr> : null}
          </tbody>;
        })}
      </Scroll> : null}
      {!row.visits ? <p className="text-xs">Arrêts non conservés pour cette validation.</p> : null}
    </> : <>
      <p className="text-xs">Préchargement : {clock(row.start_at)} → {clock(row.end_at)} · Livraison : {row.delivery_date || "Non conservée"}</p>
      <SavedOrders orders={row.links || []} loading />
    </>}
    {row.notes ? <p className="whitespace-pre-wrap text-xs">Notes : {row.notes}</p> : null}
    {detail.kind === "sheet" ? calculation ? <Calculation summary value={calculation} date={day} sheet={row} />
      : <p className="text-xs">Calcul non conservé pour cette validation.</p> : null}
    </>}
  </section>;
}
